import React from "react";
import VariantGate from './_variant_gate';
import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import Skeleton from "../components/common/skeleton";
import APIKeyEditable from "../components/my_page/api_key_editable";
import AccountKeyCard, { AccountKeyCardListError } from "../components/stocks/account_key_card";
import { getAccounts, createAccount } from "../services/stockApi";
import { showToast } from "../utils/show_toast";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import MyPageV2 from '../components/my_page_v2/my_page_v2';
import MyPageV3 from '../components/my_page_v3/my_page_v3';

// 신규 계좌 추가 시 선택 가능한 증권사 유형 (go-server model.stock_merge.go 브로커 상수와 짝을 이룬다).
const NEW_ACCOUNT_BROKER_OPTIONS = [
    { value: "kiwoom", label: "키움증권 (해외주식)" },
    { value: "kiwoom_isa", label: "키움 ISA (국내상장 ETF)" },
    { value: "mirae", label: "미래에셋증권 (수기 입력)" },
];

class MyPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            accounts: [],
            accountsLoading: true,
            accountsError: false,
            addingAccount: false,
            newAccountLabel: "",
            newAccountBroker: "kiwoom",
            creatingAccount: false,
        };
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.fetchAccounts();
    }

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    fetchAccounts = () => {
        this.setState({ accountsLoading: true, accountsError: false });
        getAccounts()
            .then((res) => this.setState({ accounts: res?.data?.accounts || [], accountsLoading: false }))
            .catch(() => this.setState({ accountsLoading: false, accountsError: true }));
    };

    onAccountUpdated = (updated) => {
        this.setState((prev) => ({
            accounts: prev.accounts.map((a) => (a.account_id === updated.account_id ? updated : a)),
        }));
    };

    onAccountDeleted = (accountId) => {
        this.setState((prev) => ({
            accounts: prev.accounts.filter((a) => a.account_id !== accountId),
        }));
    };

    onCreateAccount = () => {
        const { newAccountLabel, newAccountBroker } = this.state;
        this.setState({ creatingAccount: true });
        createAccount({ broker: newAccountBroker, label: newAccountLabel.trim() })
            .then((res) => {
                showToast({ type: "success", message: "계좌를 추가했습니다." });
                this.setState((prev) => ({
                    accounts: [...prev.accounts, res.data],
                    addingAccount: false,
                    newAccountLabel: "",
                    newAccountBroker: "kiwoom",
                    creatingAccount: false,
                }));
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "계좌 추가에 실패했습니다.";
                showToast({ type: "error", message: msg });
                this.setState({ creatingAccount: false });
            });
    };

    renderAddAccountForm() {
        const { newAccountLabel, newAccountBroker, creatingAccount } = this.state;
        const inputCls =
            "w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand";
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card-alt p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="text-xs text-content-secondary" htmlFor="new-account-broker">
                            증권사 유형
                        </label>
                        <select
                            id="new-account-broker"
                            value={newAccountBroker}
                            onChange={(e) => this.setState({ newAccountBroker: e.target.value })}
                            className={`${inputCls} mt-1`}
                        >
                            {NEW_ACCOUNT_BROKER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="text-xs text-content-secondary" htmlFor="new-account-label">
                            별칭 (선택)
                        </label>
                        <input
                            id="new-account-label"
                            type="text"
                            maxLength={40}
                            value={newAccountLabel}
                            onChange={(e) => this.setState({ newAccountLabel: e.target.value })}
                            placeholder="예: 배우자 계좌"
                            className={`${inputCls} mt-1`}
                        />
                    </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => this.setState({ addingAccount: false, newAccountLabel: "", newAccountBroker: "kiwoom" })}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 text-xs text-content-secondary hover:text-content"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={this.onCreateAccount}
                        disabled={creatingAccount}
                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 text-xs font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50"
                    >
                        {creatingAccount ? "추가 중…" : "계좌 추가"}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { collapsed, accounts, accountsLoading, accountsError, addingAccount } = this.state;

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? "sm:pl-[56px]" : "sm:pl-[220px]"}`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:mx-6 sm:my-5 sm:p-6 lg:min-w-[960px]">
                        <div className="text-sm text-content-secondary">내 정보 / API Key 관리</div>
                        <h1 className="mt-1 text-xl font-semibold text-content">마이 페이지</h1>
                        <div className="mt-6">
                            <APIKeyEditable />
                        </div>
                        <div className="mt-6">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-content-secondary">미국주식 적립 연동 — 계좌</div>
                                {!addingAccount && (
                                    <button
                                        type="button"
                                        onClick={() => this.setState({ addingAccount: true })}
                                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 text-xs font-medium text-brand-light hover:bg-brand/30"
                                    >
                                        계좌 추가
                                    </button>
                                )}
                            </div>
                            <div className="mt-3 flex flex-col gap-3">
                                {addingAccount && this.renderAddAccountForm()}
                                {accountsLoading ? (
                                    <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                                        <Skeleton variant="line" width="50%" height={14} />
                                    </div>
                                ) : accountsError ? (
                                    <AccountKeyCardListError onRetry={this.fetchAccounts} />
                                ) : accounts.length === 0 ? (
                                    <p className="text-xs text-content-muted">
                                        등록된 계좌가 없습니다. "계좌 추가"로 증권사 계좌를 등록하세요.
                                    </p>
                                ) : (
                                    accounts.map((account) => (
                                        <AccountKeyCard
                                            key={account.account_id}
                                            account={account}
                                            onUpdated={this.onAccountUpdated}
                                            onDeleted={this.onAccountDeleted}
                                            onRefetch={this.fetchAccounts}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

// DAS-70 Wave-A: variant 슬롯 3-단 확장 (v1 / v2 슬롯 / v3 정보 구조 재설계)
const MyPageV1 = MyPage;
export { MyPageV1 };
const MyPageWithVariant = (props) => (
    <VariantGate v1={MyPageV1} v2={MyPageV2} v3={MyPageV3} fallbackToV1={true} {...props} />
);
export default MyPageWithVariant;
